import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { RestaurantSettingKey, type RestaurantSetting } from "@lemmonhq/types";
import { getStringSetting } from "@app/data/helpers/settings_helpers";

dayjs.extend(utc);
dayjs.extend(timezone);

export const DEFAULT_SHIFT_START_TIME = "09:00";
export const DEFAULT_SHIFT_END_TIME = "22:00";

export type ShiftWindow = {
  /** `HH:mm` opening time as configured in restaurant settings */
  startTime: string;
  /** `HH:mm` closing time as configured in restaurant settings */
  endTime: string;
  /** Absolute instant the running shift started */
  start: Date;
  /** Absolute instant the running shift is scheduled to end */
  end: Date;
  /**
   * Date the shift is reported under (restaurant local date of its start).
   * Reports bucket a shift by `scheduled_start` in the restaurant timezone,
   * so this is the date a manager has to select to see this shift's numbers.
   */
  startLocalDate: string;
  /** Restaurant local date the shift ends on (differs for night shifts) */
  endLocalDate: string;
  spansMidnight: boolean;
};

export type ShiftDateRange = { startDate: string; endDate: string };

/** Minutes since midnight for a `HH:mm` / `HH:mm:ss` string, `null` if unparseable */
const toMinutes = (time: string | null | undefined): number | null => {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(String(time).trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

const toDayKey = (date: string | null | undefined): string =>
  (date || "").slice(0, 10);

export const getShiftTimes = (settings: RestaurantSetting[] | undefined) => ({
  startTime:
    getStringSetting(
      settings || [],
      RestaurantSettingKey.START_TIME,
      DEFAULT_SHIFT_START_TIME,
    ) || DEFAULT_SHIFT_START_TIME,
  endTime:
    getStringSetting(
      settings || [],
      RestaurantSettingKey.END_TIME,
      DEFAULT_SHIFT_END_TIME,
    ) || DEFAULT_SHIFT_END_TIME,
});

/**
 * Resolve the shift that is currently running for the restaurant, mirroring
 * `order_processing_get_shift_by_time` in SQL: `start_time`/`end_time` are wall
 * clock times in the restaurant timezone, and a shift where `end_time` is
 * before `start_time` spans midnight.
 *
 * Everything is evaluated against the restaurant's own clock, so a manager
 * checking the dashboard from another timezone gets the same answer as the POS
 * standing in the restaurant.
 *
 * Returns `null` when the restaurant is closed right now.
 */
export const getCurrentShiftWindow = (
  settings: RestaurantSetting[] | undefined,
  restaurantTimezone: string | undefined,
  now: Date = new Date(),
): ShiftWindow | null => {
  const tz = restaurantTimezone || "UTC";
  const { startTime, endTime } = getShiftTimes(settings);
  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);
  if (startMinutes === null || endMinutes === null) return null;
  // Same start and end means no usable window, same as the SQL side.
  if (startMinutes === endMinutes) return null;

  let localNow: dayjs.Dayjs;
  try {
    localNow = dayjs(now).tz(tz);
  } catch {
    localNow = dayjs(now).tz("UTC");
  }
  const nowMinutes = localNow.hour() * 60 + localNow.minute();
  const spansMidnight = startMinutes > endMinutes;

  const isOpen = spansMidnight
    ? nowMinutes >= startMinutes || nowMinutes < endMinutes
    : nowMinutes >= startMinutes && nowMinutes <= endMinutes;
  if (!isOpen) return null;

  // After midnight on a night shift the shift belongs to the previous day.
  const startDay =
    spansMidnight && nowMinutes < endMinutes
      ? localNow.subtract(1, "day")
      : localNow;
  const startLocalDate = startDay.format("YYYY-MM-DD");
  const endLocalDate = spansMidnight
    ? startDay.add(1, "day").format("YYYY-MM-DD")
    : startLocalDate;

  return {
    startTime,
    endTime,
    spansMidnight,
    startLocalDate,
    endLocalDate,
    start: dayjs.tz(`${startLocalDate} ${startTime}`, tz).toDate(),
    end: dayjs.tz(`${endLocalDate} ${endTime}`, tz).toDate(),
  };
};

/**
 * Whether the selected report range covers the running shift. Reports attribute
 * a shift to the local date it started on, so that is the date we look for.
 */
export const dateRangeCoversShift = (
  dateRange: ShiftDateRange | undefined,
  shiftWindow: ShiftWindow | null,
): boolean => {
  if (!dateRange || !shiftWindow) return false;
  const startDate = toDayKey(dateRange.startDate);
  const endDate = toDayKey(dateRange.endDate);
  if (!startDate || !endDate) return false;
  return (
    startDate <= shiftWindow.startLocalDate &&
    endDate >= shiftWindow.startLocalDate
  );
};
